import type { CartLine } from './types';

/**
 * What the basket comes to.
 *
 * This exists so the screen can show a total before the sale is sent, and it is
 * the one place the till does arithmetic the server will redo. That duplication
 * is deliberate and bounded: the cashier needs a total the instant an item is
 * scanned, and a round trip per scan would make the till feel broken on a slow
 * connection.
 *
 * What matters is that the server never trusts any of it. It reprices the whole
 * basket from the catalogue and rejects the sale if the payments do not match
 * its own figure — so the worst a bug here can do is show a number that gets
 * refused, never charge a customer the wrong amount.
 */
export interface Totals {
  subtotalRial: number;
  lineDiscountRial: number;
  basketDiscountRial: number;
  vatRial: number;
  totalRial: number;
  /** Distinct items, matching the design's «جمع کالاها (n)». */
  count: number;
}

export function totalsOf(lines: CartLine[], basketDiscountRial: number): Totals {
  let subtotal = 0;
  let lineDiscount = 0;
  let vatWeighted = 0;

  for (const l of lines) {
    const gross = Math.round(l.item.priceRial * l.quantity);
    subtotal += gross;
    lineDiscount += l.discountRial;
    // VAT per line, not one blended rate: a basket mixing exempt bread with
    // standard-rated coffee is taxed on the coffee alone.
    vatWeighted += ((gross - l.discountRial) * l.item.vatBasisPoints) / 10_000;
  }

  const net = Math.max(0, subtotal - lineDiscount - basketDiscountRial);
  // Applied proportionally, so a basket-wide discount reduces the tax with it.
  const base = subtotal - lineDiscount;
  const vat = base > 0 ? Math.round((vatWeighted * net) / base) : 0;

  return {
    subtotalRial: subtotal,
    lineDiscountRial: lineDiscount,
    basketDiscountRial,
    vatRial: vat,
    totalRial: net + vat,
    count: lines.length,
  };
}
